import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  School as SchoolIcon,
  ArrowLeft,
  Search,
  Award,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Users,
  BookOpen,
  Calendar,
  Layers,
  ChevronRight,
  Filter,
  Download,
  Printer,
  Sparkles,
  HelpCircle,
  Clock,
  ShieldCheck,
  BarChart3,
  UserX,
  Target,
  FileSpreadsheet
} from 'lucide-react';

interface SchoolAcademicProfileProps {
  schoolId: number;
  onBack?: () => void;
  onSelectSchool?: (schoolId: number) => void;
}

export const SchoolAcademicProfileView: React.FC<SchoolAcademicProfileProps> = ({
  schoolId,
  onBack,
  onSelectSchool
}) => {
  const { fetchWithAuth } = useAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<any>(null);

  // Filters
  const [academicSessionId, setAcademicSessionId] = useState<number>(3);
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [selectedStream, setSelectedStream] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'overview' | 'monthly' | 'sat' | 'comparison' | 'attendance' | 'subjects' | 'students' | 'interventions'>('overview');

  // School Quick Search / Selector
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [studentSearch, setStudentSearch] = useState<string>('');
  const [activeStudentTab, setActiveStudentTab] = useState<'at_risk' | 'top' | 'all'>('at_risk');

  const fetchProfile = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        academic_session_id: academicSessionId.toString()
      });
      if (selectedClass !== 'all') params.append('class_id', selectedClass);
      if (selectedStream !== 'all') params.append('stream', selectedStream);

      const res = await fetchWithAuth(`/api/deo/school-academic-profile/${id}?${params.toString()}`).then(r => r.json());
      if (res.success && res.data) {
        setProfileData(res.data);
      } else {
        setError(res.message || 'Failed to load school academic profile');
      }
    } catch (err: any) {
      setError(err.message || 'Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (schoolId) {
      fetchProfile(schoolId);
    }
  }, [schoolId, academicSessionId, selectedClass, selectedStream]);

  // Search schools for quick switcher
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetchWithAuth(`/api/schools?search=${encodeURIComponent(searchQuery)}`).then(r => r.json());
        if (res.success && res.data) {
          setSearchResults(res.data.slice(0, 8));
        }
      } catch (e) {
        console.error('School search failed:', e);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    if (!profileData) return;
    const { school, monthly_assessments, at_risk_students } = profileData;
    let csv = `School Academic Performance Dossier\n`;
    csv += `School Name,${school.school_name}\n`;
    csv += `School Code,${school.school_code}\n`;
    csv += `Block,${school.block}\n`;
    csv += `District Rank,${school.district_rank} of ${school.total_schools_district}\n\n`;

    csv += `MONTHLY ASSESSMENT PERFORMANCE\n`;
    csv += `Month,Students Assessed,Marks Count,Average %,Highest %,Lowest %,Pass %\n`;
    (monthly_assessments || []).forEach((m: any) => {
      csv += `${m.month},${m.students_assessed},${m.marks_count},${m.average_pct ?? 'N/A'},${m.highest_pct ?? 'N/A'},${m.lowest_pct ?? 'N/A'},${m.pass_pct ?? 'N/A'}%\n`;
    });

    csv += `\nSTUDENTS AT RISK (< 40%)\n`;
    csv += `SRN,Student Name,Father Name,Class,Stream,Average %,Weakest Subject\n`;
    (at_risk_students || []).forEach((s: any) => {
      csv += `${s.srn},${s.student_name},${s.father_name || 'N/A'},${s.class_name},${s.stream || 'N/A'},${s.avg_pct}%,${s.weakest_subject}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `School_Profile_${school.school_code}_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-[500px] flex flex-col items-center justify-center space-y-3 bg-white rounded-xl border border-slate-200 p-12">
        <div className="w-10 h-10 border-3 border-amber-400 border-t-transparent rounded-full animate-spin" />
        <div className="text-sm font-bold text-slate-800">Loading Complete Academic Profile...</div>
        <div className="text-xs text-slate-500">Querying Monthly Assessments, SAT Scores, Attendance & Student Rosters</div>
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div className="bg-white rounded-xl border border-rose-200 p-8 text-center space-y-4">
        <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto" />
        <div className="text-base font-bold text-slate-900">Unable to Load School Profile</div>
        <p className="text-xs text-rose-600">{error || 'School records could not be retrieved from database.'}</p>
        {onBack && (
          <button
            onClick={onBack}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-slate-800 text-white rounded-lg text-xs font-bold hover:bg-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Return to District Dashboard</span>
          </button>
        )}
      </div>
    );
  }

  const {
    school,
    kpis,
    monthly_assessments = [],
    sat_assessments = [],
    sat_vs_assessment_comparison = [],
    attendance = {},
    subject_performance = [],
    class_performance = [],
    performance_distribution = {},
    at_risk_students = [],
    performing_well_students = [],
    all_students = [],
    interventions = []
  } = profileData;

  const filteredAllStudents = all_students.filter((s: any) => {
    if (!studentSearch.trim()) return true;
    const q = studentSearch.toLowerCase();
    return (
      s.student_name?.toLowerCase().includes(q) ||
      s.srn?.toLowerCase().includes(q) ||
      s.father_name?.toLowerCase().includes(q) ||
      s.class_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6 print:space-y-4">
      {/* 1. TOP NAVIGATION & SCHOOL SELECTOR BAR */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
        <div className="flex items-center space-x-3">
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>District Overview</span>
            </button>
          )}
          <span className="text-xs font-bold text-slate-400">/</span>
          <span className="text-xs font-bold text-slate-600">School Profile</span>
        </div>

        {/* Quick School Switcher Autocomplete */}
        <div className="relative w-full lg:w-96">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Switch School: Search Name, Code, Block..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 hover:bg-slate-100 focus:bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F2C59]"
            />
          </div>

          {searchOpen && searchResults.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg z-50 max-h-64 overflow-y-auto">
              <div className="p-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                Matched Schools ({searchResults.length})
              </div>
              {searchResults.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSearchQuery('');
                    setSearchOpen(false);
                    if (onSelectSchool) onSelectSchool(s.id);
                    else fetchProfile(s.id);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center justify-between text-xs border-b border-slate-50 last:border-0 transition-colors"
                >
                  <div>
                    <div className="font-bold text-slate-800">{s.school_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">Code: {s.school_code} • Block: {s.block}</div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                    {s.category || 'Senior Secondary'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold shadow-2xs"
          >
            <Download className="w-3.5 h-3.5 text-slate-500" />
            <span>Export CSV</span>
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-[#0F2C59] hover:bg-[#1E3A8A] text-white rounded-lg text-xs font-bold shadow-2xs"
          >
            <Printer className="w-3.5 h-3.5 text-amber-400" />
            <span>Print Dossier</span>
          </button>
        </div>
      </div>

      {/* 2. SCHOOL HEADER BANNER */}
      <div className="bg-gradient-to-r from-[#0F2C59] via-[#1E3A8A] to-[#0F2C59] rounded-2xl text-white p-6 shadow-md border border-slate-700/50">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start space-x-4">
            <div className="p-3.5 bg-white/10 rounded-2xl backdrop-blur-xs border border-white/10 shrink-0">
              <SchoolIcon className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <span className="text-[11px] font-bold px-2.5 py-0.5 bg-amber-400 text-slate-900 rounded-md uppercase tracking-wider font-mono">
                  {school.school_code}
                </span>
                <span className="text-[11px] font-bold px-2.5 py-0.5 bg-white/20 text-white rounded-md">
                  Block: {school.block}
                </span>
                <span className="text-[11px] font-bold px-2.5 py-0.5 bg-emerald-500/30 text-emerald-300 border border-emerald-400/30 rounded-md">
                  {school.category || 'Government Senior Secondary'}
                </span>
              </div>
              <h1 className="text-xl lg:text-2xl font-black text-white mt-1.5 tracking-tight">
                {school.school_name}
              </h1>
              <p className="text-xs text-slate-300 mt-1 flex items-center space-x-2">
                <span>District Education Office, Nuh • Department of School Education, Haryana</span>
              </p>
            </div>
          </div>

          {/* Comparative Standing Badges */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="bg-white/10 backdrop-blur-xs rounded-xl p-3 border border-white/10 text-center min-w-[110px]">
              <div className="text-[10px] uppercase font-bold text-amber-300 tracking-wider">District Rank</div>
              <div className="text-xl font-black text-white mt-0.5">
                #{school.district_rank} <span className="text-xs font-normal text-slate-300">/ {school.total_schools_district}</span>
              </div>
              <div className="text-[10px] text-slate-300 mt-0.5">Top {Math.round((school.district_rank / school.total_schools_district) * 100)}% in Nuh</div>
            </div>

            <div className="bg-white/10 backdrop-blur-xs rounded-xl p-3 border border-white/10 text-center min-w-[110px]">
              <div className="text-[10px] uppercase font-bold text-emerald-300 tracking-wider">Block Rank</div>
              <div className="text-xl font-black text-white mt-0.5">
                #{school.block_rank} <span className="text-xs font-normal text-slate-300">/ {school.total_schools_block}</span>
              </div>
              <div className="text-[10px] text-slate-300 mt-0.5">{school.block} Block</div>
            </div>
          </div>
        </div>

        {/* Filter Ribbon inside Header */}
        <div className="mt-6 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center space-x-2 flex-wrap gap-y-2">
            <span className="text-slate-300 font-bold flex items-center space-x-1">
              <Filter className="w-3.5 h-3.5 text-amber-400" />
              <span>Scope Filters:</span>
            </span>

            {/* Academic Session */}
            <select
              value={academicSessionId}
              onChange={(e) => setAcademicSessionId(parseInt(e.target.value, 10))}
              className="bg-white/10 border border-white/20 rounded-md px-2.5 py-1 text-white text-xs font-bold focus:outline-none focus:bg-[#0F2C59]"
            >
              <option value={3} className="text-slate-900">Session 2026–27 (Current)</option>
              <option value={2} className="text-slate-900">Session 2025–26</option>
              <option value={1} className="text-slate-900">Session 2024–25</option>
            </select>

            {/* Class */}
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-md px-2.5 py-1 text-white text-xs font-bold focus:outline-none focus:bg-[#0F2C59]"
            >
              <option value="all" className="text-slate-900">All Classes (10 & 12)</option>
              <option value="1" className="text-slate-900">Class 10</option>
              <option value="2" className="text-slate-900">Class 12</option>
            </select>

            {/* Stream */}
            {selectedClass === '2' && (
              <select
                value={selectedStream}
                onChange={(e) => setSelectedStream(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-md px-2.5 py-1 text-white text-xs font-bold focus:outline-none focus:bg-[#0F2C59]"
              >
                <option value="all" className="text-slate-900">All Streams</option>
                <option value="Science" className="text-slate-900">Science</option>
                <option value="Commerce" className="text-slate-900">Commerce</option>
                <option value="Arts" className="text-slate-900">Arts</option>
              </select>
            )}
          </div>

          <div className="text-[11px] text-slate-300 font-medium">
            Generated: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* 3. 8 HIGH-LEVEL KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {/* 1. Total Students */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Students</div>
          <div className="text-xl font-black text-slate-900 mt-1">{kpis.total_students}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Enrolled: {kpis.total_enrolled}</div>
        </div>

        {/* 2. Students Assessed */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Assessed</div>
          <div className="text-xl font-black text-slate-900 mt-1">{kpis.students_assessed}</div>
          <div className="text-[10px] text-emerald-600 font-bold mt-0.5">
            {kpis.total_students > 0 ? `${Math.round((kpis.students_assessed / kpis.total_students) * 100)}% coverage` : '0%'}
          </div>
        </div>

        {/* 3. Monthly Assessment Avg % */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Monthly Avg %</div>
          <div className="text-xl font-black text-slate-900 mt-1">
            {kpis.monthly_assessment_avg_pct !== null ? `${kpis.monthly_assessment_avg_pct}%` : (
              <span className="text-xs text-slate-400 font-bold">Data Not Available</span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">Pass: {kpis.pass_percentage ?? 'N/A'}%</div>
        </div>

        {/* 4. SAT Average % */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">SAT Avg %</div>
          <div className="text-xl font-black text-slate-900 mt-1">
            {kpis.sat_avg_pct !== null ? `${kpis.sat_avg_pct}%` : (
              <span className="text-xs text-slate-400 font-bold">Data Not Available</span>
            )}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">District SAT Exam</div>
        </div>

        {/* 5. Student Attendance % */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Student Attend.</div>
          <div className="text-xl font-black text-slate-900 mt-1">
            {kpis.student_attendance_pct !== null ? `${kpis.student_attendance_pct}%` : (
              <span className="text-xs text-slate-400 font-bold">Data Not Available</span>
            )}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            {attendance.reported_records ? `${attendance.reported_records} logs` : 'Unreported'}
          </div>
        </div>

        {/* 6. Teacher Attendance % */}
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Teacher Attend.</div>
          <div className="text-xl font-black text-slate-900 mt-1">
            {kpis.teacher_attendance_pct !== null ? `${kpis.teacher_attendance_pct}%` : (
              <span className="text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 font-bold">
                Data Not Available
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">No Daily Punch Log</div>
        </div>

        {/* 7. Students At Risk (< 40%) */}
        <div className="bg-white p-3.5 rounded-xl border border-rose-200 bg-rose-50/20 shadow-2xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 flex items-center justify-between">
            <span>At-Risk</span>
            <AlertTriangle className="w-3 h-3 text-rose-500" />
          </div>
          <div className="text-xl font-black text-rose-600 mt-1">{kpis.students_at_risk_count}</div>
          <div className="text-[10px] text-rose-500 font-bold mt-0.5">&lt; 40% Score Tier</div>
        </div>

        {/* 8. High Performers (>= 75%) */}
        <div className="bg-white p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/20 shadow-2xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center justify-between">
            <span>High Performers</span>
            <Sparkles className="w-3 h-3 text-emerald-500" />
          </div>
          <div className="text-xl font-black text-emerald-600 mt-1">{kpis.students_performing_well_count}</div>
          <div className="text-[10px] text-emerald-600 font-bold mt-0.5">&ge; 75% Score Tier</div>
        </div>
      </div>

      {/* 4. SECTION TABS */}
      <div className="flex border-b border-slate-200 bg-white rounded-t-xl px-2 overflow-x-auto">
        {[
          { id: 'overview', label: '1. Overview & Board Readiness' },
          { id: 'monthly', label: '2. Monthly Assessment' },
          { id: 'sat', label: '3. SAT Performance' },
          { id: 'comparison', label: '4. SAT vs Assessment' },
          { id: 'attendance', label: '5. Attendance Analytics' },
          { id: 'subjects', label: '6. Subject Mastery' },
          { id: 'students', label: `7. Student Rosters (${all_students.length})` },
          { id: 'interventions', label: `8. Targeted Action Plan (${interventions.length})` }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`py-3 px-4 text-xs font-bold border-b-2 whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'border-[#0F2C59] text-[#0F2C59]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB CONTENT AREAS */}

      {/* TAB 1: OVERVIEW & BOARD READINESS */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Board Exam Performance Distribution */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs lg:col-span-2">
              <h3 className="text-sm font-bold text-slate-900 flex items-center justify-between">
                <span>Board Readiness Tier Distribution</span>
                <span className="text-xs font-normal text-slate-500">Based on evaluated students</span>
              </h3>

              <div className="grid grid-cols-5 gap-3 mt-4 text-center">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="text-xs font-bold text-emerald-800">Outstanding</div>
                  <div className="text-[10px] text-emerald-600 font-bold">&ge; 90%</div>
                  <div className="text-xl font-black text-emerald-700 mt-1">{performance_distribution.outstanding || 0}</div>
                </div>

                <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="text-xs font-bold text-teal-800">Good</div>
                  <div className="text-[10px] text-teal-600 font-bold">75–89%</div>
                  <div className="text-xl font-black text-teal-700 mt-1">{performance_distribution.good || 0}</div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-xs font-bold text-blue-800">Satisfactory</div>
                  <div className="text-[10px] text-blue-600 font-bold">60–74%</div>
                  <div className="text-xl font-black text-blue-700 mt-1">{performance_distribution.satisfactory || 0}</div>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="text-xs font-bold text-amber-800">Needs Support</div>
                  <div className="text-[10px] text-amber-600 font-bold">40–59%</div>
                  <div className="text-xl font-black text-amber-700 mt-1">{performance_distribution.needs_improvement || 0}</div>
                </div>

                <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
                  <div className="text-xs font-bold text-rose-800">Critical At-Risk</div>
                  <div className="text-[10px] text-rose-600 font-bold">&lt; 40%</div>
                  <div className="text-xl font-black text-rose-700 mt-1">{performance_distribution.critical || 0}</div>
                </div>
              </div>

              {/* Progress Bar Breakdown */}
              <div className="mt-6">
                <div className="h-4 w-full bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
                  {kpis.students_assessed > 0 ? (
                    <>
                      <div style={{ width: `${((performance_distribution.outstanding || 0) / kpis.students_assessed) * 100}%` }} className="bg-emerald-500" title="Outstanding" />
                      <div style={{ width: `${((performance_distribution.good || 0) / kpis.students_assessed) * 100}%` }} className="bg-teal-500" title="Good" />
                      <div style={{ width: `${((performance_distribution.satisfactory || 0) / kpis.students_assessed) * 100}%` }} className="bg-blue-500" title="Satisfactory" />
                      <div style={{ width: `${((performance_distribution.needs_improvement || 0) / kpis.students_assessed) * 100}%` }} className="bg-amber-500" title="Needs Improvement" />
                      <div style={{ width: `${((performance_distribution.critical || 0) / kpis.students_assessed) * 100}%` }} className="bg-rose-500" title="Critical" />
                    </>
                  ) : (
                    <div className="w-full bg-slate-200 flex items-center justify-center text-[10px] text-slate-500">No Assessments Recorded</div>
                  )}
                </div>
                <div className="flex justify-between text-[11px] text-slate-500 mt-2 font-medium">
                  <span>Pass Threshold: 33%</span>
                  <span>Board Target: &ge; 75%</span>
                </div>
              </div>
            </div>

            {/* Class 10 vs 12 Cards */}
            <div className="space-y-3">
              {class_performance.map((cp: any) => (
                <div key={cp.class_id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#0F2C59] uppercase tracking-wider">{cp.class_name}</span>
                    <span className="text-[10px] px-2 py-0.5 bg-slate-100 font-bold rounded text-slate-600">
                      {cp.assessed_students} / {cp.total_students} Assessed
                    </span>
                  </div>
                  <div className="flex items-baseline space-x-2 mt-2">
                    <span className="text-2xl font-black text-slate-900">
                      {cp.average_pct !== null ? `${cp.average_pct}%` : 'Data Not Available'}
                    </span>
                    <span className="text-xs text-slate-500">Average Score</span>
                  </div>
                  <div className="mt-2 text-xs text-slate-600 flex justify-between border-t border-slate-100 pt-2">
                    <span>Passing Rate: <strong>{cp.pass_pct ?? 'N/A'}%</strong></span>
                    <span>Coverage: <strong>{cp.total_students > 0 ? Math.round((cp.assessed_students / cp.total_students) * 100) : 0}%</strong></span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Snapshot of Interventions */}
          {interventions.length > 0 && (
            <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <h3 className="text-sm font-bold text-amber-900">Immediate Academic Interventions Flagged for this School</h3>
                </div>
                <button
                  onClick={() => setActiveTab('interventions')}
                  className="text-xs font-bold text-amber-800 hover:underline flex items-center space-x-1"
                >
                  <span>View Full Action Plan</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {interventions.slice(0, 2).map((it: any) => (
                  <div key={it.id} className="bg-white p-3.5 rounded-lg border border-amber-200 text-xs shadow-2xs">
                    <div className="font-bold text-slate-900">{it.title}</div>
                    <p className="text-slate-600 mt-1 text-[11px] leading-relaxed">{it.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: MONTHLY ASSESSMENT ANALYSIS */}
      {activeTab === 'monthly' && (
        <div className="space-y-6">
          {/* Month-Wise Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Complete Month-Wise Monthly Assessment Performance</h3>
                <p className="text-xs text-slate-500 mt-0.5">Evaluated scores across all subjects and board classes</p>
              </div>
              <span className="text-xs font-bold text-slate-600 bg-white px-2.5 py-1 rounded border border-slate-200">
                Academic Session: 2026–27
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                    <th className="py-3 px-4">Month</th>
                    <th className="py-3 px-4 text-center">Students Assessed</th>
                    <th className="py-3 px-4 text-center">Total Marks Entries</th>
                    <th className="py-3 px-4 text-center">Average %</th>
                    <th className="py-3 px-4 text-center">Highest %</th>
                    <th className="py-3 px-4 text-center">Lowest %</th>
                    <th className="py-3 px-4 text-center">Pass % (&ge; 33%)</th>
                    <th className="py-3 px-4 text-center">At-Risk (&lt; 40%)</th>
                    <th className="py-3 px-4 text-center">Top (&ge; 75%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                  {monthly_assessments.length > 0 ? (
                    monthly_assessments.map((row: any) => (
                      <tr key={row.month} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4 font-bold text-[#0F2C59]">{row.month}</td>
                        <td className="py-3 px-4 text-center">{row.students_assessed}</td>
                        <td className="py-3 px-4 text-center text-slate-500">{row.marks_count}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded-full font-bold ${
                            row.average_pct >= 75 ? 'bg-emerald-100 text-emerald-800' :
                            row.average_pct >= 50 ? 'bg-blue-100 text-blue-800' :
                            'bg-rose-100 text-rose-800'
                          }`}>
                            {row.average_pct !== null ? `${row.average_pct}%` : 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-emerald-700 font-bold">{row.highest_pct ?? 'N/A'}%</td>
                        <td className="py-3 px-4 text-center text-rose-600 font-bold">{row.lowest_pct ?? 'N/A'}%</td>
                        <td className="py-3 px-4 text-center font-bold">{row.pass_pct ?? 'N/A'}%</td>
                        <td className="py-3 px-4 text-center">
                          {row.at_risk_count > 0 ? (
                            <span className="px-2 py-0.5 bg-rose-50 text-rose-700 font-bold rounded border border-rose-200">
                              {row.at_risk_count} students
                            </span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {row.performing_well_count > 0 ? (
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 font-bold rounded border border-emerald-200">
                              {row.performing_well_count} students
                            </span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-slate-400">
                        No Monthly Assessment data recorded for this school in the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly Trend Visualizer */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
            <h3 className="text-sm font-bold text-slate-900">Monthly Performance Trendline</h3>
            <p className="text-xs text-slate-500 mt-0.5">Average score % across academic months</p>

            <div className="grid grid-cols-4 md:grid-cols-9 gap-3 mt-4">
              {['May', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February'].map((m) => {
                const row = monthly_assessments.find((r: any) => r.month === m);
                const score = row?.average_pct ?? null;
                return (
                  <div key={m} className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-center">
                    <div className="text-[11px] font-bold text-slate-600">{m}</div>
                    <div className="text-lg font-black text-slate-900 mt-1">
                      {score !== null ? `${score}%` : <span className="text-xs text-slate-300 font-normal">—</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {row ? `${row.students_assessed} students` : 'Pending'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: SAT PERFORMANCE ANALYSIS */}
      {activeTab === 'sat' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Student Assessment Test (SAT) Examination Records</h3>
                <p className="text-xs text-slate-500 mt-0.5">State-standard standardized examinations for board preparation</p>
              </div>
              <span className="text-xs font-bold text-slate-600 bg-white px-2.5 py-1 rounded border border-slate-200">
                Max Marks: Dynamic (August: 20/100, September: 40)
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                    <th className="py-3 px-4">SAT Examination</th>
                    <th className="py-3 px-4 text-center">Configured Max Marks</th>
                    <th className="py-3 px-4 text-center">Students Assessed</th>
                    <th className="py-3 px-4 text-center">Average Score %</th>
                    <th className="py-3 px-4 text-center">Highest Score %</th>
                    <th className="py-3 px-4 text-center">Lowest Score %</th>
                    <th className="py-3 px-4 text-center">Pass Rate (&ge; 33%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                  {sat_assessments.length > 0 ? (
                    sat_assessments.map((row: any) => (
                      <tr key={row.month} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4 font-bold text-[#0F2C59]">{row.month} SAT Exam</td>
                        <td className="py-3 px-4 text-center font-mono font-bold text-slate-700">{row.maximum_marks}</td>
                        <td className="py-3 px-4 text-center font-bold">{row.students_assessed}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded-full">
                            {row.average_pct}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-emerald-700 font-bold">{row.highest_pct}%</td>
                        <td className="py-3 px-4 text-center text-rose-600 font-bold">{row.lowest_pct}%</td>
                        <td className="py-3 px-4 text-center font-bold">{row.pass_pct}%</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-400">
                        No SAT exam records logged for this school in the selected session.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: SAT VS MONTHLY ASSESSMENT COMPARISON */}
      {activeTab === 'comparison' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Month-Wise SAT vs Monthly Assessment Comparative Analysis</h3>
                <p className="text-xs text-slate-500 mt-0.5">Correlation and variance between internal testing and standardized SAT exams</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                    <th className="py-3 px-4">Month</th>
                    <th className="py-3 px-4 text-center">Monthly Assessment Avg %</th>
                    <th className="py-3 px-4 text-center">Monthly Students</th>
                    <th className="py-3 px-4 text-center">SAT Exam Avg %</th>
                    <th className="py-3 px-4 text-center">SAT Students</th>
                    <th className="py-3 px-4 text-center">Variance (SAT - Assessment)</th>
                    <th className="py-3 px-4">Diagnostic Interpretation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                  {sat_vs_assessment_comparison.map((row: any) => (
                    <tr key={row.month} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 font-bold text-[#0F2C59]">{row.month}</td>
                      <td className="py-3 px-4 text-center">
                        {row.assessment_avg_pct !== null ? `${row.assessment_avg_pct}%` : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-3 px-4 text-center text-slate-500">{row.assessment_students || '—'}</td>
                      <td className="py-3 px-4 text-center font-bold">
                        {row.sat_avg_pct !== null ? `${row.sat_avg_pct}%` : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-3 px-4 text-center text-slate-500">{row.sat_students || '—'}</td>
                      <td className="py-3 px-4 text-center">
                        {row.difference !== null ? (
                          <span className={`px-2 py-0.5 rounded font-bold ${
                            row.difference > 0 ? 'bg-emerald-100 text-emerald-800' :
                            row.difference < 0 ? 'bg-rose-100 text-rose-800' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {row.difference > 0 ? `+${row.difference}%` : `${row.difference}%`}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-[11px]">
                        {row.difference !== null ? (
                          row.difference > 10 ? 'SAT performance significantly exceeds internal regular tests.' :
                          row.difference < -10 ? 'SAT scores lower than internal school assessments; review exam rigor.' :
                          'Closely aligned performance.'
                        ) : (
                          <span className="text-slate-400">Single or pending assessment dataset</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: ATTENDANCE ANALYTICS */}
      {activeTab === 'attendance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Student Attendance %</div>
              <div className="text-2xl font-black text-slate-900 mt-1">
                {attendance.attendance_pct !== null ? `${attendance.attendance_pct}%` : 'Data Not Available'}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">Overall Recorded Rate</div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Boys Attendance %</div>
              <div className="text-2xl font-black text-blue-700 mt-1">
                {attendance.boys_attendance_pct !== null ? `${attendance.boys_attendance_pct}%` : 'Data Not Available'}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">Male Students</div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Girls Attendance %</div>
              <div className="text-2xl font-black text-rose-700 mt-1">
                {attendance.girls_attendance_pct !== null ? `${attendance.girls_attendance_pct}%` : 'Data Not Available'}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">Female Students</div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Teacher Attendance %</div>
              <div className="text-2xl font-black text-slate-400 mt-1">
                {attendance.teacher_attendance_pct !== null ? `${attendance.teacher_attendance_pct}%` : (
                  <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 font-bold inline-block">
                    Data Not Available
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">No daily teacher punch logs</div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
            <h3 className="text-sm font-bold text-slate-900">Attendance Session Summary</h3>
            <p className="text-xs text-slate-500 mt-0.5">Logged attendance events from official attendance registers</p>
            <div className="grid grid-cols-3 gap-4 mt-4 text-center">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="text-xs font-bold text-slate-500">Total Student Attendance Records</div>
                <div className="text-xl font-bold text-slate-800 mt-1">{attendance.reported_records || 0}</div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="text-xs font-bold text-emerald-700">Present Instances</div>
                <div className="text-xl font-bold text-emerald-800 mt-1">{attendance.present_count || 0}</div>
              </div>
              <div className="p-3 bg-rose-50 rounded-lg border border-rose-200">
                <div className="text-xs font-bold text-rose-700">Absent Instances</div>
                <div className="text-xl font-bold text-rose-800 mt-1">{attendance.absent_count || 0}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 6: SUBJECT MASTERY */}
      {activeTab === 'subjects' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Subject-Wise Performance & Remedial Focus</h3>
                <p className="text-xs text-slate-500 mt-0.5">Identifies academic subjects requiring targeted board exam practice</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                    <th className="py-3 px-4">Subject</th>
                    <th className="py-3 px-4">Class & Stream</th>
                    <th className="py-3 px-4 text-center">Students Assessed</th>
                    <th className="py-3 px-4 text-center">Marks Entries</th>
                    <th className="py-3 px-4 text-center">Average Score %</th>
                    <th className="py-3 px-4 text-center">Highest %</th>
                    <th className="py-3 px-4 text-center">Lowest %</th>
                    <th className="py-3 px-4 text-center">Passing Rate (&ge; 33%)</th>
                    <th className="py-3 px-4">Diagnostic Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                  {subject_performance.length > 0 ? (
                    subject_performance.map((s: any) => (
                      <tr key={`${s.subject_id}_${s.class_name}_${s.stream}`} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4 font-bold text-slate-900">{s.subject_name}</td>
                        <td className="py-3 px-4 text-slate-600">
                          {s.class_name} {s.stream ? `• ${s.stream}` : ''}
                        </td>
                        <td className="py-3 px-4 text-center">{s.students_count}</td>
                        <td className="py-3 px-4 text-center text-slate-500">{s.marks_count}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2.5 py-0.5 rounded-full font-bold ${
                            s.avg_pct >= 75 ? 'bg-emerald-100 text-emerald-800' :
                            s.avg_pct >= 50 ? 'bg-blue-100 text-blue-800' :
                            'bg-rose-100 text-rose-800'
                          }`}>
                            {s.avg_pct}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-emerald-700 font-bold">{s.max_pct}%</td>
                        <td className="py-3 px-4 text-center text-rose-600 font-bold">{s.min_pct}%</td>
                        <td className="py-3 px-4 text-center font-bold">{s.pass_pct}%</td>
                        <td className="py-3 px-4">
                          {s.avg_pct < 40 ? (
                            <span className="px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded text-[10px] font-bold">
                              Critical Remedial Focus
                            </span>
                          ) : s.avg_pct < 60 ? (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[10px] font-bold">
                              Practice Needed
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[10px] font-bold">
                              Satisfactory
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-slate-400">
                        No subject performance records available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 7: STUDENT ROSTERS */}
      {activeTab === 'students' && (
        <div className="space-y-6">
          {/* Sub-tabs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-3 rounded-xl border border-slate-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setActiveStudentTab('at_risk')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                  activeStudentTab === 'at_risk'
                    ? 'bg-rose-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                At-Risk Students ({at_risk_students.length})
              </button>
              <button
                onClick={() => setActiveStudentTab('top')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                  activeStudentTab === 'top'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Top Performers ({performing_well_students.length})
              </button>
              <button
                onClick={() => setActiveStudentTab('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                  activeStudentTab === 'all'
                    ? 'bg-[#0F2C59] text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All Enrolled Students ({all_students.length})
              </button>
            </div>

            {/* Student Search Input */}
            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Search SRN, Name, Father..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-[#0F2C59]"
              />
            </div>
          </div>

          {/* At-Risk Students Sub-tab */}
          {activeStudentTab === 'at_risk' && (
            <div className="bg-white rounded-xl border border-rose-200 shadow-xs overflow-hidden">
              <div className="p-4 bg-rose-50/60 border-b border-rose-200 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-rose-600" />
                  <span className="text-sm font-bold text-rose-900">
                    Students Requiring Immediate Board Exam Remediation (&lt; 40% Average)
                  </span>
                </div>
                <span className="text-xs font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded">
                  {at_risk_students.length} Students
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                      <th className="py-3 px-4">SRN</th>
                      <th className="py-3 px-4">Student Name</th>
                      <th className="py-3 px-4">Father Name</th>
                      <th className="py-3 px-4">Class & Stream</th>
                      <th className="py-3 px-4 text-center">Average %</th>
                      <th className="py-3 px-4">Weakest Subject</th>
                      <th className="py-3 px-4 text-center">Remedial Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                    {at_risk_students.map((st: any) => (
                      <tr key={st.id} className="hover:bg-rose-50/40 transition-colors">
                        <td className="py-3 px-4 font-mono font-bold text-slate-700">{st.srn}</td>
                        <td className="py-3 px-4 font-bold text-slate-900">{st.student_name}</td>
                        <td className="py-3 px-4 text-slate-500">{st.father_name || '—'}</td>
                        <td className="py-3 px-4 text-slate-600">{st.class_name} {st.stream ? `• ${st.stream}` : ''}</td>
                        <td className="py-3 px-4 text-center font-bold text-rose-600 bg-rose-50/50">{st.avg_pct}%</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 bg-rose-100 text-rose-800 rounded font-bold text-[11px]">
                            {st.weakest_subject}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded">
                            Assign 1-on-1 Mentor
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Performers Sub-tab */}
          {activeStudentTab === 'top' && (
            <div className="bg-white rounded-xl border border-emerald-200 shadow-xs overflow-hidden">
              <div className="p-4 bg-emerald-50/60 border-b border-emerald-200 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-bold text-emerald-900">
                    Top High-Performing Candidates (&ge; 75% Score Tier)
                  </span>
                </div>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                  {performing_well_students.length} Students
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                      <th className="py-3 px-4">SRN</th>
                      <th className="py-3 px-4">Student Name</th>
                      <th className="py-3 px-4">Father Name</th>
                      <th className="py-3 px-4">Class & Stream</th>
                      <th className="py-3 px-4 text-center">Average %</th>
                      <th className="py-3 px-4">Strongest Subject</th>
                      <th className="py-3 px-4 text-center">Merit Recommendation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                    {performing_well_students.map((st: any) => (
                      <tr key={st.id} className="hover:bg-emerald-50/40 transition-colors">
                        <td className="py-3 px-4 font-mono font-bold text-slate-700">{st.srn}</td>
                        <td className="py-3 px-4 font-bold text-slate-900">{st.student_name}</td>
                        <td className="py-3 px-4 text-slate-500">{st.father_name || '—'}</td>
                        <td className="py-3 px-4 text-slate-600">{st.class_name} {st.stream ? `• ${st.stream}` : ''}</td>
                        <td className="py-3 px-4 text-center font-bold text-emerald-700 bg-emerald-50/50">{st.avg_pct}%</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[11px]">
                            {st.strongest_subject}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
                            Super-50 Merit Batch
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All Enrolled Students Sub-tab */}
          {activeStudentTab === 'all' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <span className="text-sm font-bold text-slate-900">
                  Complete School Student Ledger ({filteredAllStudents.length} Students)
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                      <th className="py-3 px-4">SRN</th>
                      <th className="py-3 px-4">Student Name</th>
                      <th className="py-3 px-4">Father Name</th>
                      <th className="py-3 px-4">Gender</th>
                      <th className="py-3 px-4">Class</th>
                      <th className="py-3 px-4">Stream</th>
                      <th className="py-3 px-4 text-center">Tests Taken</th>
                      <th className="py-3 px-4 text-center">Overall Average %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                    {filteredAllStudents.slice(0, 50).map((st: any) => (
                      <tr key={st.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4 font-mono font-bold text-slate-700">{st.srn}</td>
                        <td className="py-3 px-4 font-bold text-slate-900">{st.student_name}</td>
                        <td className="py-3 px-4 text-slate-500">{st.father_name || '—'}</td>
                        <td className="py-3 px-4 text-slate-600">{st.gender || '—'}</td>
                        <td className="py-3 px-4 text-slate-600">{st.class_name}</td>
                        <td className="py-3 px-4 text-slate-600">{st.stream || 'N/A'}</td>
                        <td className="py-3 px-4 text-center text-slate-500">{st.total_tests}</td>
                        <td className="py-3 px-4 text-center font-bold">
                          {st.average_pct !== null ? `${st.average_pct}%` : <span className="text-slate-300">Pending</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 8: TARGETED ACTION PLAN & INTERVENTIONS */}
      {activeTab === 'interventions' && (
        <div className="space-y-6">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
            <h3 className="text-sm font-bold text-slate-900">Customized Academic Intervention Framework</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Automated diagnostics generated from genuine assessment scores and attendance logs
            </p>

            <div className="space-y-4 mt-5">
              {interventions.length > 0 ? (
                interventions.map((it: any) => (
                  <div
                    key={it.id}
                    className={`p-4 rounded-xl border text-xs ${
                      it.priority === 'CRITICAL'
                        ? 'bg-rose-50/50 border-rose-200'
                        : it.priority === 'HIGH'
                        ? 'bg-amber-50/50 border-amber-200'
                        : 'bg-blue-50/50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          it.priority === 'CRITICAL' ? 'bg-rose-200 text-rose-900' :
                          it.priority === 'HIGH' ? 'bg-amber-200 text-amber-900' :
                          'bg-blue-200 text-blue-900'
                        }`}>
                          {it.priority} PRIORITY
                        </span>
                        <h4 className="font-bold text-slate-900 text-sm">{it.title}</h4>
                      </div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{it.type}</span>
                    </div>

                    <p className="text-slate-700 mt-2 text-xs leading-relaxed">{it.description}</p>

                    <div className="mt-3 pt-3 border-t border-slate-200/60 flex items-center justify-between">
                      <div className="text-[11px] font-bold text-[#0F2C59]">
                        Recommended Action: <span className="font-normal text-slate-700">{it.action}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-slate-400">
                  No critical interventions required. School performance is operating within normal parameters.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
